'use client';

import {
  createContext,
  useContext,
  useMemo,
  useState,
  type Dispatch,
  type ReactNode,
  type SetStateAction,
} from 'react';
import { DeliveryQuote } from '../../types/deliveryQuote.types';
import { DeliveryInstallmentPlan } from '../../types/installments.types';
import { BillingInformationFormFields } from '../types/billingInformation.types';
import { CustomerFields } from '../types/customerInformation.types';

type FormFields = BillingInformationFormFields & CustomerFields;
type OptionalFormValues = {
  [K in keyof FormFields]?: FormFields[K];
};
type FormFieldsContextType = {
  formValues: OptionalFormValues | null;
  setFormValues: Dispatch<SetStateAction<OptionalFormValues | null>>;
  deliveryQuote: DeliveryQuote | null;
  setDeliveryQuote: Dispatch<SetStateAction<DeliveryQuote | null>>;
  installmentsStoneDelivery: DeliveryInstallmentPlan[] | null;
  setInstallmentsStoneDelivery: Dispatch<SetStateAction<DeliveryInstallmentPlan[] | null>>;
};

const FormTypedValues = createContext<FormFieldsContextType | null>(null);

export const FormTypedValuesProvider = ({
  children,
}: {
  children: ReactNode;
}) => {
  const [formValues, setFormValues] = useState<OptionalFormValues | null>(null);
  const [deliveryQuote, setDeliveryQuote] = useState<DeliveryQuote | null>(null);
  const [installmentsStoneDelivery, setInstallmentsStoneDelivery] = useState<DeliveryInstallmentPlan[] | null>(null);
  const contextValue = useMemo(
    () => ({ formValues, setFormValues, deliveryQuote, setDeliveryQuote, installmentsStoneDelivery, setInstallmentsStoneDelivery }),
    [formValues, deliveryQuote, installmentsStoneDelivery]
  );

  return (
    <FormTypedValues.Provider value={contextValue}>
      {children}
    </FormTypedValues.Provider>
  );
};

export const useFormTypedValues = () => {
  const context = useContext(FormTypedValues);

  if (!context) {
    throw new Error(
      'useFormTypedValues must be used within a FormTypedValuesProvider'
    );
  }

  return context;
};
